// Module 12 — Administrative Module (ADM-01 to ADM-11)
import React, { useState, useEffect, useCallback } from 'react';
import AppShell from '../../components/layout/AppShell';
import api from '../../services/api';
import { adminApi } from '../../services/api';
import { fmtDT } from '../../utils/helpers';
import { Spinner, EmptyState, Modal } from '../../components/common';
import toast from 'react-hot-toast';
import { Plus, RefreshCw, ToggleLeft, ToggleRight, Bell, Trash2, Edit2} from 'lucide-react';

// ── ADM-01/02 Service Areas & Zones ──────────────────────────────────────────
export function ServiceAreasPage() {
  const [pincodes, setPincodes] = useState([]);
  const [loading, setLoad]      = useState(true);
  const [search, setSearch]     = useState('');
  const [showAdd, setShowAdd]   = useState(false);
  const [form, setForm]         = useState({ code:'', city:'', state:'', country:'PK', isServiceable:true, isRemote:false, isRestricted:false, zoneId:'', hubId:'' });
  const set = k => e => setForm(f=>({...f,[k]:e.target.type==='checkbox'?e.target.checked:e.target.value}));

  const load = useCallback(async () => {
    setLoad(true);
    const q = search ? '?search='+encodeURIComponent(search) : '?limit=100';
    api.get('/admin/pincodes'+q).then(r=>r.data).then(r=>{ if(r.success) setPincodes(r.data||[]); }).finally(()=>setLoad(false));
  },[search]);
  useEffect(()=>{ const t=setTimeout(load,300); return()=>clearTimeout(t); },[load]);

  const create = async () => {
    if (!form.code||!form.city) { toast.error('Pincode and city required'); return; }
    try {
      const r = await api.post('/admin/pincodes', form).then(r=>r.data);
      if (r.success) { toast.success('Pincode added'); setShowAdd(false); load(); }
    } catch(e) { toast.error(e.response?.data?.message||'Failed'); }
  };

  const toggleServiceable = async (pincodeId, current) => {
    try {
      await api.patch('/admin/pincodes/'+pincodeId, { isServiceable:!current }).then(r=>r.data);
      load();
    } catch { toast.error('Failed'); }
  };

  return (
    <AppShell title="Service Areas & Pincodes" actions={<button className="btn btn-primary btn-sm" onClick={()=>setShowAdd(true)}><Plus size={14}/> Add Pincode</button>}>
      <div style={{ marginBottom:12 }}>
        <input className="form-control" placeholder="Search by pincode, city..." style={{ maxWidth:300 }}
          value={search} onChange={e=>setSearch(e.target.value)}/>
      </div>
      <div className="card">
        <div className="table-wrap">
          {loading ? <Spinner /> : pincodes.length ? (
            <table>
              <thead><tr><th>Pincode</th><th>City</th><th>State</th><th>Country</th><th>Serviceable</th><th>Remote</th><th>Restricted</th><th></th></tr></thead>
              <tbody>
                {pincodes.map(p=>(
                  <tr key={p.pincode_id}>
                    <td><code style={{ fontWeight:700 }}>{p.code}</code></td>
                    <td style={{ fontWeight:500 }}>{p.city}</td>
                    <td className="text-sm">{p.state||'—'}</td>
                    <td className="text-sm">{p.country}</td>
                    <td>
                      <button onClick={()=>toggleServiceable(p.pincode_id, p.is_serviceable)} style={{ background:'none', border:'none', cursor:'pointer' }}>
                        {p.is_serviceable ? <ToggleRight size={20} color="var(--success)"/> : <ToggleLeft size={20} color="var(--gray-300)"/>}
                      </button>
                    </td>
                    <td>{p.is_remote?<span className="badge badge-warning" style={{ fontSize:'.65rem' }}>Remote</span>:'—'}</td>
                    <td>{p.is_restricted?<span className="badge badge-danger" style={{ fontSize:'.65rem' }}>Restricted</span>:'—'}</td>
                    <td>
                      <button className="btn btn-danger btn-sm" style={{ fontSize:'.7rem' }}
                        onClick={async()=>{ if(!window.confirm('Delete pincode '+p.code+'?')) return; try{ await api.delete('/admin/pincodes/'+p.pincode_id); toast.success('Deleted'); load(); }catch(e){ toast.error(e.response?.data?.message||'Failed'); } }}>
                        <Trash2 size={11}/>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <EmptyState icon="📍" title="No pincodes" />}
        </div>
      </div>
      <Modal open={showAdd} onClose={()=>setShowAdd(false)} title="Add Pincode">
        <div className="modal-body">
          <div className="form-row">
            <div className="form-group"><label className="form-label">Pincode <span className="req">*</span></label><input className="form-control" value={form.code} onChange={set('code')}/></div>
            <div className="form-group"><label className="form-label">City <span className="req">*</span></label><input className="form-control" value={form.city} onChange={set('city')}/></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">State</label><input className="form-control" value={form.state} onChange={set('state')}/></div>
            <div className="form-group"><label className="form-label">Country</label>
              <select className="form-control" value={form.country} onChange={set('country')}>
                {['PK','AE','SA','GB','US'].map(c=><option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">Zone ID (optional)</label><input className="form-control" placeholder="zone UUID" value={form.zoneId||''} onChange={set('zoneId')}/></div>
            <div className="form-group"><label className="form-label">Hub ID (optional)</label><input className="form-control" placeholder="hub UUID" value={form.hubId||''} onChange={set('hubId')}/></div>
          </div>
          <div style={{ display:'flex', gap:16, marginTop:8 }}>
            {[['isServiceable','Serviceable'],['isRemote','Remote Area'],['isRestricted','Restricted']].map(([k,l])=>(
              <label key={k} style={{ display:'flex', alignItems:'center', gap:6, cursor:'pointer', fontSize:'.85rem' }}>
                <input type="checkbox" checked={form[k]} onChange={set(k)}/> {l}
              </label>
            ))}
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={()=>setShowAdd(false)}>Cancel</button>
          <button className="btn btn-primary" onClick={create}>Add Pincode</button>
        </div>
      </Modal>
    </AppShell>
  );
}

// ── ADM-05/06 Role & Permission Management ────────────────────────────────────
export function RolePermissionsPage() {
  const ROLES = [
    { role:'ADMIN',      desc:'Full system access',                    color:'#7c3aed', permissions:['All modules','User management','System configuration','Financial posting','Audit access'] },
    { role:'MANAGEMENT', desc:'Executive dashboard, reports, approvals', color:'#1e40af', permissions:['Exec dashboard','MIS reports','Approval workflows','Revenue analytics','Multi-branch view'] },
    { role:'STAFF',      desc:'Operations, booking, scanning',          color:'#065f46', permissions:['Shipment booking','Scan terminal','Manifests','Pickup assignment','Ops dashboard'] },
    { role:'RIDER',      desc:'Pickup and delivery operations',         color:'#92400e', permissions:['Delivery sheet','COD collection','Status updates','Route viewer'] },
    { role:'FINANCE',    desc:'Financial management',                   color:'#166534', permissions:['Invoicing','Vouchers','GL','Reports','COD remittance','Bank reconciliation'] },
    { role:'HR',         desc:'Human resource management',             color:'#9d174d', permissions:['Employees','Attendance','Payroll','Leave management','Recruitment'] },
    { role:'CS',         desc:'Customer service & support',            color:'#1e3a8a', permissions:['Ticket management','Claims processing','CSAT tracking','Communication logs'] },
    { role:'SALES',      desc:'Sales and CRM',                         color:'#064e3b', permissions:['Lead management','Corporate accounts','Sales visits','Targets tracking'] },
    { role:'B2C',        desc:'Individual customer portal',            color:'#374151', permissions:['Book shipments','Track shipments','My invoices','Support tickets'] },
    { role:'B2B',        desc:'Corporate customer portal',             color:'#1f2937', permissions:['Bulk booking','Corporate billing','Rate contracts','API access'] },
  ];
  return (
    <AppShell title="Role & Permission Matrix">
      <div style={{ display:'grid', gap:10 }}>
        {ROLES.map(r=>(
          <div key={r.role} className="card">
            <div style={{ display:'flex', alignItems:'center', gap:16, padding:'12px 16px' }}>
              <div style={{ width:44, height:44, borderRadius:10, background:r.color+'20', display:'flex', alignItems:'center', justifyContent:'center', border:'2px solid '+r.color+'40', flexShrink:0 }}>
                <span style={{ fontWeight:800, fontSize:'.82rem', color:r.color }}>{r.role.slice(0,2)}</span>
              </div>
              <div style={{ flex:1 }}>
                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                  <span style={{ fontWeight:700, fontSize:'.9rem' }}>{r.role}</span>
                  <span style={{ padding:'2px 8px', borderRadius:10, fontSize:'.65rem', fontWeight:600, background:r.color+'18', color:r.color }}>{r.desc}</span>
                </div>
                <div style={{ display:'flex', flexWrap:'wrap', gap:4, marginTop:6 }}>
                  {r.permissions.map(p=>(
                    <span key={p} style={{ padding:'2px 7px', borderRadius:4, background:'var(--gray-50)', border:'1px solid var(--gray-200)', fontSize:'.65rem', color:'var(--gray-600)' }}>
                      ✓ {p}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      <div style={{ marginTop:16, padding:'12px 16px', background:'#FFFBEB', borderRadius:8, border:'1px solid #FDE68A', fontSize:'.8rem' }}>
        Role assignments are managed in the Employees section. Contact the System Administrator to modify role permissions.
      </div>
    </AppShell>
  );
}

// ── ADM-07 Workflow Configuration ─────────────────────────────────────────────
export function WorkflowConfigPage() {
  const [flags, setFlags] = useState([]);
  const [loading, setLoad] = useState(true);

  useEffect(()=>{
    api.get('/admin/feature-flags').then(r=>r.data).then(r=>{ if(r.success) setFlags(r.data||[]); }).finally(()=>setLoad(false));
  },[]);

  const toggle = async (key, current) => {
    try {
      await api.patch('/admin/feature-flags/'+key, { isEnabled:!current }).then(r=>r.data);
      setFlags(prev=>prev.map(f=>f.flag_key===key?{...f,is_enabled:!current}:f));
      toast.success((current?'Disabled':'Enabled')+' — '+key);
    } catch { toast.error('Failed'); }
  };

  const WORKFLOW_FLAGS = [
    { key:'APPROVAL_CREDIT_INCREASE', label:'Credit Limit Approvals', desc:'Require management approval for credit limit increases' },
    { key:'APPROVAL_DISCOUNT',        label:'Discount Approvals',      desc:'Require manager approval for discounts above 5%' },
    { key:'APPROVAL_REFUND',          label:'Refund Approvals',        desc:'Require finance approval for refunds above PKR 5,000' },
    { key:'AUTO_CLAIM_ESCALATION',    label:'Auto Claim Escalation',   desc:'Auto-escalate claims unresolved after 72 hours' },
    { key:'AI_ROUTES',                label:'AI Route Optimization',   desc:'Enable AI-powered route optimization for deliveries' },
    { key:'AI_FRAUD_DETECTION',       label:'Fraud Detection',         desc:'Enable real-time fraud scanning on bookings' },
    { key:'BULK_BOOKING',             label:'Bulk CSV Booking',        desc:'Allow corporate customers to upload bulk shipment CSV' },
    { key:'POSTPAID_BILLING',         label:'Postpaid Billing',        desc:'Allow postpaid billing model for corporate accounts' },
  ];

  const getFlag = (key) => flags.find(f=>f.flag_key===key);

  return (
    <AppShell title="Workflow & Feature Configuration">
      <div style={{ display:'grid', gap:8 }}>
        {WORKFLOW_FLAGS.map(wf=>{
          const f = getFlag(wf.key);
          const enabled = f ? f.is_enabled : false;
          return (
            <div key={wf.key} style={{ padding:'14px 16px', borderRadius:8, border:'1px solid var(--gray-200)', display:'flex', justifyContent:'space-between', alignItems:'center', background:'white' }}>
              <div>
                <div style={{ fontWeight:600, fontSize:'.9rem' }}>{wf.label}</div>
                <div style={{ fontSize:'.75rem', color:'var(--gray-400)', marginTop:2 }}>{wf.desc}</div>
                <code style={{ fontSize:'.65rem', color:'var(--gray-300)', marginTop:3, display:'block' }}>{wf.key}</code>
              </div>
              <button onClick={()=>toggle(wf.key, enabled)} style={{ background:'none', border:'none', cursor:'pointer', flexShrink:0 }}>
                {enabled ? <ToggleRight size={28} color="var(--success)"/> : <ToggleLeft size={28} color="var(--gray-300)"/>}
              </button>
            </div>
          );
        })}
      </div>
    </AppShell>
  );
}

// ── ADM-08 Notification Settings ──────────────────────────────────────────────
export function NotificationSettingsPage() {
  const [config, setConfig] = useState({ smtpHost:'', smtpPort:'587', smtpUser:'', twilioSid:'', twilioFrom:'', emailFrom:'' });
  const [testing, setTesting] = useState(false);
  const set = k => e => setConfig(c=>({...c,[k]:e.target.value}));

  const test = async (channel) => {
    setTesting(true);
    try {
      await api.post('/admin/notifications/test', { channel, recipient:'test@ecl.pk' }).then(r=>r.data);
      toast.success('Test '+channel+' sent (check notification log)');
    } catch(e) { toast.error(e.response?.data?.message||'Test failed — check gateway credentials'); }
    finally { setTesting(false); }
  };

  return (
    <AppShell title="Notification Settings (ADM-08)">
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
        <div className="card">
          <div className="card-header"><span className="card-title"><Bell size={14} style={{ marginRight:6, display:'inline' }}/>Email (SMTP / SendGrid)</span></div>
          <div className="card-body">
            <div className="form-group"><label className="form-label">SMTP Host</label><input className="form-control" placeholder="smtp.sendgrid.net" value={config.smtpHost} onChange={set('smtpHost')}/></div>
            <div className="form-row">
              <div className="form-group"><label className="form-label">Port</label><input className="form-control" value={config.smtpPort} onChange={set('smtpPort')}/></div>
              <div className="form-group"><label className="form-label">API Key / Password</label><input className="form-control" type="password" placeholder="SG.xxxx" value={config.smtpUser} onChange={set('smtpUser')}/></div>
            </div>
            <div className="form-group"><label className="form-label">From Email</label><input className="form-control" placeholder="noreply@ecl.pk" value={config.emailFrom} onChange={set('emailFrom')}/></div>
            <div className="alert alert-info" style={{ fontSize:'.78rem', padding:'8px 12px', marginTop:8 }}>
              Set SMTP_PASS in backend .env file for live email. Set REACT_APP_API_URL in frontend .env.production for production build.
            </div>
            <button className="btn btn-outline btn-sm" onClick={()=>test('email')} disabled={testing}>Test Email</button>
          </div>
        </div>
        <div className="card">
          <div className="card-header"><span className="card-title">📱 SMS (Twilio)</span></div>
          <div className="card-body">
            <div className="form-group"><label className="form-label">Twilio SID</label><input className="form-control" placeholder="ACxxxx" value={config.twilioSid} onChange={set('twilioSid')}/></div>
            <div className="form-group"><label className="form-label">From Number</label><input className="form-control" placeholder="+12345678" value={config.twilioFrom} onChange={set('twilioFrom')}/></div>
            <div className="alert alert-info" style={{ fontSize:'.78rem', padding:'8px 12px', marginTop:8 }}>
              Set TWILIO_SID, TWILIO_TOKEN, TWILIO_FROM in backend .env to enable live SMS sending.
            </div>
            <button className="btn btn-outline btn-sm" onClick={()=>test('sms')} disabled={testing}>Test SMS</button>
          </div>
        </div>
      </div>

      <div className="card" style={{ marginTop:16 }}>
        <div className="card-header"><span className="card-title">Notification Templates</span></div>
        <div className="table-wrap">
          <table>
            <thead><tr><th>Event</th><th>Channel</th><th>Subject</th><th>Status</th></tr></thead>
            <tbody>
              {[
                ['BOOKING_CONFIRMED','SMS+Email','Shipment {tracking_id} booked'],
                ['OUT_FOR_DELIVERY','SMS','Your shipment is out for delivery today'],
                ['DELIVERED','SMS+Email','Shipment {tracking_id} delivered successfully'],
                ['DELIVERY_ATTEMPTED','SMS','Delivery attempt failed — we will retry'],
                ['RTO_INITIATED','Email','Shipment being returned to origin'],
                ['INVOICE_GENERATED','Email','Invoice {invoice_no} generated — due {due_date}'],
              ].map(([event, channel, subject])=>(
                <tr key={event}>
                  <td><code style={{ fontSize:'.75rem' }}>{event}</code></td>
                  <td className="text-sm">{channel}</td>
                  <td style={{ fontSize:'.8rem', color:'var(--gray-600)' }}>{subject}</td>
                  <td><span className="badge badge-success" style={{ fontSize:'.62rem' }}>Active</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </AppShell>
  );
}

// ── ADM-10/11 System Logs ─────────────────────────────────────────────────────
export function AuditLogPage() {
  const [logs, setLogs]     = useState([]);
  const [loading, setLoad]  = useState(true);
  const [filters, setFilters] = useState({ table:'', action:'', actorId:'' });

  const load = useCallback(async () => {
    setLoad(true);
    const p = new URLSearchParams(Object.fromEntries(Object.entries(filters).filter(([,v])=>v)));
    adminApi.getAuditLogs ? adminApi.getAuditLogs().then(r=>{ if(r.success) setLogs(r.data||[]); }).finally(()=>setLoad(false))
    : api.get('/admin/audit?'+p+'&limit=100').then(r=>r.data).then(r=>{ if(r.success) setLogs(r.data||[]); }).finally(()=>setLoad(false));
  },[filters]);
  useEffect(()=>{ load(); },[load]);

  const ACTION_COLOR = { INSERT:'var(--success)', UPDATE:'var(--info)', DELETE:'var(--danger)', VIEW:'var(--gray-400)' };

  return (
    <AppShell title="Audit Log (ADM-10/11)" actions={<button className="btn btn-outline btn-sm" onClick={load}><RefreshCw size={13}/> Refresh</button>}>
      <div className="filters-bar" style={{ marginBottom:12 }}>
        <input className="form-control" placeholder="Table name" style={{ width:160 }} value={filters.table} onChange={e=>setFilters(f=>({...f,table:e.target.value}))}/>
        <select className="form-control" style={{ width:120 }} value={filters.action} onChange={e=>setFilters(f=>({...f,action:e.target.value}))}>
          <option value="">All Actions</option>
          {['INSERT','UPDATE','DELETE','VIEW'].map(a=><option key={a} value={a}>{a}</option>)}
        </select>
        <button className="btn btn-outline btn-sm" onClick={load}>Filter</button>
      </div>
      <div className="card">
        <div className="table-wrap">
          {loading ? <Spinner /> : logs.length ? (
            <table>
              <thead><tr><th>Time</th><th>Table</th><th>Action</th><th>Actor</th><th>IP</th><th>Changes</th></tr></thead>
              <tbody>
                {logs.map(l=>(
                  <tr key={l.log_id}>
                    <td style={{ fontFamily:'monospace', fontSize:'.75rem' }}>{fmtDT(l.created_at)}</td>
                    <td><code style={{ fontSize:'.75rem' }}>{l.table_name}</code></td>
                    <td><span style={{ padding:'2px 7px', borderRadius:4, fontSize:'.68rem', fontWeight:700, background:(ACTION_COLOR[l.action]||'#888')+'18', color:ACTION_COLOR[l.action]||'#888' }}>{l.action}</span></td>
                    <td className="text-sm">{l.actor_type==='employee'?'👤 ':l.actor_type==='customer'?'🏢 ':''}{l.actor_id?.slice(0,8)||'system'}</td>
                    <td style={{ fontFamily:'monospace', fontSize:'.72rem', color:'var(--gray-400)' }}>{l.ip_address||'—'}</td>
                    <td style={{ fontSize:'.72rem', color:'var(--gray-500)', maxWidth:200 }}>
                      {l.new_values ? JSON.stringify(l.new_values).slice(0,60)+'…' : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <EmptyState icon="📋" title="No audit logs" />}
        </div>
      </div>
    </AppShell>
  );
}

// ── ADM-09 Integration Configuration ─────────────────────────────────────────
export function IntegrationConfigPage() {
  const INTEGRATIONS = [
    { name:'Shopify', icon:'🛍️', status:'Available', desc:'Sync orders from Shopify store to auto-create shipments', endpoint:'/integrations/shopify', configured:false },
    { name:'WooCommerce', icon:'🛒', status:'Available', desc:'Auto-import WooCommerce orders as ECL shipments', endpoint:'/integrations/woo', configured:false },
    { name:'Daraz', icon:'🏪', status:'Available', desc:'Daraz seller shipment sync via API', endpoint:'/integrations/daraz', configured:false },
    { name:'SendGrid', icon:'📧', status:'Active', desc:'Email gateway for transactional notifications', endpoint:null, configured:true },
    { name:'Twilio', icon:'📱', status:'Active', desc:'SMS gateway for status notifications', endpoint:null, configured:false },
    { name:'Google Maps', icon:'🗺️', status:'Future', desc:'Live rider GPS tracking and route visualization', endpoint:null, configured:false },
    { name:'NADRA', icon:'🪪', status:'Future', desc:'KYC verification via NADRA VERISYS API', endpoint:null, configured:false },
    { name:'1BILL', icon:'💳', status:'Future', desc:'Online payment gateway integration', endpoint:null, configured:false },
  ];
  return (
    <AppShell title="Integration Configuration (ADM-09)">
      <div style={{ display:'grid', gridTemplateColumns:'repeat(2,1fr)', gap:12 }}>
        {INTEGRATIONS.map(ig=>(
          <div key={ig.name} style={{ padding:'14px 16px', borderRadius:8, border:'1px solid var(--gray-200)', display:'flex', gap:14, alignItems:'flex-start', background:'white' }}>
            <div style={{ fontSize:28, flexShrink:0 }}>{ig.icon}</div>
            <div style={{ flex:1 }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
                <div style={{ fontWeight:600 }}>{ig.name}</div>
                <span style={{ padding:'2px 8px', borderRadius:10, fontSize:'.62rem', fontWeight:700,
                  background:ig.status==='Active'?'#DCFCE7':ig.status==='Available'?'#DBEAFE':'#F3F4F6',
                  color:ig.status==='Active'?'#166534':ig.status==='Available'?'#1E40AF':'#6B7280' }}>
                  {ig.status}
                </span>
              </div>
              <div style={{ fontSize:'.78rem', color:'var(--gray-500)', marginTop:4 }}>{ig.desc}</div>
              {ig.status !== 'Future' && (
                <div style={{ marginTop:8 }}>
                  {ig.configured
                    ? <span style={{ fontSize:'.72rem', color:'var(--success)' }}>✓ Connected</span>
                    : <button className="btn btn-outline btn-sm" style={{ fontSize:'.7rem' }}>Configure</button>
                  }
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </AppShell>
  );
}

// ── Zones Management Page ─────────────────────────────────────────────────────
export function ZonesPage() {
  const [zones, setZones]   = useState([]);
  const [hubs, setHubs]     = useState([]);
  const [loading, setLoad]  = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editZone, setEditZone] = useState(null);
  const [form, setForm]     = useState({ code:'', name:'', country:'PK', hubId:'' });
  const set = k => e => setForm(f=>({...f,[k]:e.target.value}));

  const load = useCallback(async () => {
    setLoad(true);
    Promise.all([
      api.get('/zones').then(r=>r.data).catch(()=>({ success:false, data:[] })),
      api.get('/admin/hubs').then(r=>r.data).catch(()=>({ success:false, data:[] })),
    ]).then(([z,h])=>{ if(z.success) setZones(z.data||[]); if(h.success) setHubs(h.data||[]); }).finally(()=>setLoad(false));
  },[]);
  useEffect(()=>{ load(); },[load]);

  const save = async () => {
    if (!form.code||!form.name) { toast.error('Code and name required'); return; }
    try {
      if (editZone) {
        await api.patch('/admin/zones/'+editZone.zone_id, { name:form.name, country:form.country });
      } else {
        await api.post('/admin/zones', form);
      }
      toast.success(editZone?'Zone updated':'Zone created');
      setShowAdd(false); setEditZone(null);
      setForm({ code:'', name:'', country:'PK', hubId:'' });
      load();
    } catch(e) { toast.error(e.response?.data?.message||'Failed'); }
  };

  const deleteZone = async (z) => {
    if (!window.confirm('Delete zone '+z.code+'?')) return;
    try { await api.delete('/admin/zones/'+z.zone_id); toast.success('Deleted'); load(); }
    catch(e) { toast.error(e.response?.data?.message||'Cannot delete zone in use'); }
  };

  return (
    <AppShell title="Zones" actions={<button className="btn btn-primary btn-sm" onClick={()=>setShowAdd(true)}><Plus size={14}/> Add Zone</button>}>
      <div className="card">
        <div className="table-wrap">
          {loading ? <Spinner /> : zones.length ? (
            <table>
              <thead><tr><th>Code</th><th>Name</th><th>Country</th><th>Hub</th><th>Status</th><th>Actions</th></tr></thead>
              <tbody>
                {zones.map(z=>(
                  <tr key={z.zone_id}>
                    <td><code style={{ fontWeight:700 }}>{z.code}</code></td>
                    <td style={{ fontWeight:500 }}>{z.name}</td>
                    <td>{z.country}</td>
                    <td className="text-sm">{z.hub_name||'—'}</td>
                    <td><span className={`badge ${z.is_active?'badge-success':'badge-gray'}`} style={{ fontSize:'.65rem' }}>{z.is_active?'Active':'Inactive'}</span></td>
                    <td><div style={{ display:'flex', gap:4 }}>
                      <button className="btn btn-outline btn-sm" style={{ fontSize:'.7rem' }} onClick={()=>{ setEditZone(z); setForm({ code:z.code, name:z.name, country:z.country||'PK', hubId:z.hub_id||'' }); setShowAdd(true); }}><Edit2 size={11}/></button>
                      <button className="btn btn-danger btn-sm" style={{ fontSize:'.7rem' }} onClick={()=>deleteZone(z)}><Trash2 size={11}/></button>
                    </div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <EmptyState icon="🗺️" title="No zones configured" />}
        </div>
      </div>
      <Modal open={showAdd} onClose={()=>{ setShowAdd(false); setEditZone(null); }} title={editZone?'Edit Zone':'Add Zone'}>
        <div className="modal-body">
          <div className="form-row">
            <div className="form-group"><label className="form-label">Code <span className="req">*</span></label><input className="form-control" placeholder="Z-KHI" value={form.code} onChange={set('code')} disabled={!!editZone}/></div>
            <div className="form-group"><label className="form-label">Name <span className="req">*</span></label><input className="form-control" placeholder="Karachi Zone" value={form.name} onChange={set('name')}/></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">Country</label>
              <select className="form-control" value={form.country} onChange={set('country')}>
                {['PK','AE','SA','GB','US'].map(c=><option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="form-group"><label className="form-label">Hub</label>
              <select className="form-control" value={form.hubId} onChange={set('hubId')}>
                <option value="">— No hub —</option>
                {hubs.map(h=><option key={h.hub_id} value={h.hub_id}>{h.name}</option>)}
              </select>
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={()=>{ setShowAdd(false); setEditZone(null); }}>Cancel</button>
          <button className="btn btn-primary" onClick={save}>{editZone?'Save Changes':'Create Zone'}</button>
        </div>
      </Modal>
    </AppShell>
  );
}
